import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_BASE = '/api';
const api = {
  get: (p: string) => axios.get(API_BASE + p),
};

type TenantStatus = {
  tenant_id: number; enabled: boolean; sso_enabled: boolean;
  presence_sync_enabled: boolean; contacts_sync_enabled: boolean;
  meetings_sync_enabled: boolean; graph_health: string; updated_at: string;
};

type AuditEntry = {
  id: number; tenant_id: number; user_id: number; event_type: string;
  details: any; ip_address: string; created_at: string;
};

const HEALTH_COLORS: Record<string, string> = {
  healthy: '#22c55e', error: '#ef4444', not_configured: '#64748b',
};
const HEALTH_LABELS: Record<string, string> = {
  healthy: '✓ Healthy', error: '✗ Error', not_configured: '— Not configured',
};

function Badge({ text, color }: { text: string; color: string }) {
  return (
    <span style={{ padding: '3px 10px', borderRadius: 10, background: color + '22',
      color, fontSize: 11, fontWeight: 600, border: `1px solid ${color}40` }}>
      {text}
    </span>
  );
}

export default function TeamsAdminPage({ user }: any) {
  const [tenants, setTenants] = useState<TenantStatus[]>([]);
  const [audit, setAudit] = useState<AuditEntry[]>([]);
  const [tab, setTab] = useState<'overview' | 'audit'>('overview');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get('/teams/admin/status').then((r: any) => setTenants(r.data)).catch(() => {}),
      api.get('/teams/admin/audit?limit=50').then((r: any) => setAudit(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  const healthy = tenants.filter(t => t.graph_health === 'healthy').length;
  const errors  = tenants.filter(t => t.graph_health === 'error').length;
  const enabled = tenants.filter(t => t.enabled).length;

  const tabBtn = (id: 'overview' | 'audit', label: string) => (
    <button onClick={() => setTab(id)}
      style={{ padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 13,
        background: tab === id ? '#6366f1' : '#1e293b', color: tab === id ? '#fff' : '#94a3b8' }}>
      {label}
    </button>
  );

  const fmtDate = (d: string) => d ? new Date(d).toLocaleString() : '—';

  return (
    <div style={{ padding: '28px 32px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 28 }}>
        <span style={{ fontSize: 28 }}>🔷</span>
        <div>
          <h2 style={{ color: '#f1f5f9', margin: 0, fontSize: 22, fontWeight: 700 }}>Teams Admin</h2>
          <p style={{ color: '#64748b', margin: '2px 0 0', fontSize: 12 }}>Platform-wide Microsoft Teams status and audit log</p>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 14, marginBottom: 28, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Tenants', value: tenants.length, color: '#6366f1' },
          { label: 'Teams Enabled', value: enabled, color: '#22c55e' },
          { label: 'Graph Healthy', value: healthy, color: '#0f766e' },
          { label: 'Graph Errors', value: errors, color: '#ef4444' },
        ].map(s => (
          <div key={s.label} style={{ background: '#1e293b', borderRadius: 12, padding: '18px 24px',
            minWidth: 140, flex: 1, borderLeft: `4px solid ${s.color}` }}>
            <div style={{ color: '#94a3b8', fontSize: 11, textTransform: 'uppercase', letterSpacing: 1 }}>{s.label}</div>
            <div style={{ color: '#f1f5f9', fontSize: 28, fontWeight: 700, marginTop: 6 }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        {tabBtn('overview', 'Tenant Overview')}
        {tabBtn('audit', `Audit Log (${audit.length})`)}
      </div>

      {loading ? (
        <div style={{ color: '#64748b', padding: 40, textAlign: 'center' }}>Loading...</div>
      ) : tab === 'overview' ? (
        <div style={{ background: '#1e293b', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#0f172a' }}>
                {['Tenant ID', 'Status', 'Graph API', 'SSO', 'Presence', 'Contacts', 'Meetings', 'Last Updated'].map(h => (
                  <th key={h} style={{ color: '#64748b', padding: '12px 16px', textAlign: 'left', fontSize: 11,
                    fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {tenants.length === 0 ? (
                <tr><td colSpan={8} style={{ color: '#475569', padding: '32px 16px', textAlign: 'center' }}>
                  No Teams configurations found. Tenants must save config in Teams Settings to appear here.
                </td></tr>
              ) : tenants.map(t => (
                <tr key={t.tenant_id} style={{ borderTop: '1px solid #1e3a5f' }}>
                  <td style={{ padding: '12px 16px', color: '#94a3b8', fontSize: 13 }}>#{t.tenant_id}</td>
                  <td style={{ padding: '12px 16px' }}>
                    <Badge text={t.enabled ? 'Enabled' : 'Disabled'} color={t.enabled ? '#22c55e' : '#64748b'} />
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <span style={{ color: HEALTH_COLORS[t.graph_health] || '#64748b', fontSize: 12, fontWeight: 600 }}>
                      {HEALTH_LABELS[t.graph_health] || '—'}
                    </span>
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <Badge text={t.sso_enabled ? 'On' : 'Off'} color={t.sso_enabled ? '#6366f1' : '#334155'} />
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <Badge text={t.presence_sync_enabled ? 'On' : 'Off'} color={t.presence_sync_enabled ? '#6366f1' : '#334155'} />
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <Badge text={t.contacts_sync_enabled ? 'On' : 'Off'} color={t.contacts_sync_enabled ? '#6366f1' : '#334155'} />
                  </td>
                  <td style={{ padding: '12px 16px' }}>
                    <Badge text={t.meetings_sync_enabled ? 'On' : 'Off'} color={t.meetings_sync_enabled ? '#6366f1' : '#334155'} />
                  </td>
                  <td style={{ padding: '12px 16px', color: '#475569', fontSize: 11 }}>{fmtDate(t.updated_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ background: '#1e293b', borderRadius: 12, overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#0f172a' }}>
                {['Time', 'Tenant', 'User', 'Event', 'Details', 'IP'].map(h => (
                  <th key={h} style={{ color: '#64748b', padding: '12px 16px', textAlign: 'left', fontSize: 11,
                    fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {audit.length === 0 ? (
                <tr><td colSpan={6} style={{ color: '#475569', padding: '32px 16px', textAlign: 'center' }}>No audit events yet</td></tr>
              ) : audit.map(a => (
                <tr key={a.id} style={{ borderTop: '1px solid #1e3a5f' }}>
                  <td style={{ padding: '10px 16px', color: '#475569', fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDate(a.created_at)}</td>
                  <td style={{ padding: '10px 16px', color: '#94a3b8', fontSize: 12 }}>#{a.tenant_id}</td>
                  <td style={{ padding: '10px 16px', color: '#94a3b8', fontSize: 12 }}>{a.user_id || '—'}</td>
                  <td style={{ padding: '10px 16px' }}>
                    <code style={{ background: '#0f172a', color: '#c084fc', padding: '2px 8px', borderRadius: 4, fontSize: 11 }}>
                      {a.event_type}
                    </code>
                  </td>
                  <td style={{ padding: '10px 16px', color: '#64748b', fontSize: 11, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {JSON.stringify(a.details)}
                  </td>
                  <td style={{ padding: '10px 16px', color: '#475569', fontSize: 11 }}>{a.ip_address || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
